import classNames from "classnames";
import { useTranslations } from "next-intl";
import { CSSProperties, forwardRef, HTMLAttributes, useEffect, useId, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";

import PhoneInputForm from "~/components/form/phone-input-form";
import crossCountryUtils from "~/utils/cross-country-utils";

import styles from "./controlled-phone-input.module.scss";

type ControlledPhoneInputProps = {
  control: Control<any>;
  disabled?: boolean;
  error?: string;
  hidden?: boolean;
  informativeMessage?: string;
  isOptional?: boolean;
  label?: string;
  maxLength?: number;
  maxOptions?: number;
  minTypedChars?: number;
  name: string;
  placeholder?: string;
  onClearErrors?: () => void;
  successMessage?: string;
};

// forwardRef<HTMLElement, HTMLProps<HTMLElement> & PagePopupTriggerProps>
const ControlledPhoneInput = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & ControlledPhoneInputProps>(
  function ControlledPhoneInput(props: ControlledPhoneInputProps, ref) {
    const t = useTranslations();
    const id = useId();
    const refLabel = useRef<HTMLLabelElement | null>(null);
    const [isInputFocused, setInputFocused] = useState(false);
    const [widthLabel, setWidthLabel] = useState<number | undefined>();
    const styleLabelLine = {
      "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
    } as CSSProperties;

    useEffect(() => {
      setWidthLabel(refLabel.current?.offsetWidth);
    }, [props.isOptional]);

    return (
      <div
        ref={ref}
        className={classNames(
          styles.container,
          props.error ? styles.containerWithErrors : null,
          isInputFocused ? styles.containerInFocus : null,
          props.hidden ? styles.hidden : null
        )}
      >
        <div className={styles.wrapperLabelField}>
          <label
            htmlFor={id}
            ref={refLabel}
            style={styleLabelLine}
            className={styles.labelField}
            onMouseDown={(e) => e.preventDefault()}
          >
            {props.label}
            {props.isOptional ? (
              ""
            ) : (
              <span className={styles.mandatory} aria-hidden>
                {t("generic.form.mandatory_short")}
              </span>
            )}
          </label>
        </div>
        <div
          className={classNames(styles.wrapperInputField, props.disabled ? styles.wrapperInputFieldUneditable : null)}
          onFocus={() => setInputFocused(true)}
          onBlur={(e) => {
            // if relatedTarget is not defined or not inside wrapperInputField
            if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
              setInputFocused(false);
            }
          }}
        >
          <Controller
            name={props.name}
            control={props.control}
            render={({ field: { value, onChange } }) => {
              return (
                <PhoneInputForm
                  value={value}
                  onChange={(e) => {
                    const number = (e.number || "").replace(/\s+/g, "");
                    onChange(
                      number ? `${crossCountryUtils.getPhonePrefixFromCountry(e.country!) || ""} ${number}` : ""
                    );
                  }}
                  maxLength={props.maxLength}
                  placeholder={props.placeholder}
                  inputId={id}
                />
              );
            }}
          />
        </div>
        {props.error ? (
          <p id={`${id}-error`} className={styles.errorMessage}>
            {props.error}
          </p>
        ) : props.successMessage ? (
          <p className={styles.successMessage}>{props.successMessage}</p>
        ) : props.informativeMessage ? (
          <p className={styles.informativeMessage}>{props.informativeMessage}</p>
        ) : (
          <></>
        )}
      </div>
    );
  }
);

export default ControlledPhoneInput;
